import type { FC, ReactNode } from 'react'
import type { StaffBoardEntry } from '@/lib/carwash/selectors'

import Link from 'next/link'
import {
    CarFront,
    ChevronRight,
    PencilLine,
    ToggleRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/kit/avatar'
import { Button } from '@/components/kit/button'
import { Plate } from '@/components/kit/plate'
import { StaffStatusBadge } from '@/components/kit/badge'
import { JobClock } from '@/components/jobs/job-timing'
import { formatPhone } from '@/lib/carwash/phone'
import { formatTime } from '@/lib/carwash/format'

interface StaffCardProps {
    entry: StaffBoardEntry
    onAssign: () => void
    onAvailability: () => void
    onEdit: () => void
}

export const StaffCard: FC<StaffCardProps> = ({ entry, onAssign, onAvailability, onEdit }): ReactNode => {
    const { staff, status, currentJob, jobs } = entry
    const inactive = status === 'inactive'

    return (
        <article className={cn('flex flex-col rounded-2xl border border-border bg-card shadow-card', inactive && 'opacity-75')}>
            <Link
                href={`/staff/${staff.id}`}
                className='flex items-center gap-3 rounded-t-2xl p-4 pb-3 transition-colors hover:bg-slate-50/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
            >
                <Avatar name={staff.name} photo={staff.photo} size='lg' />

                <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                        <p className='truncate font-heading text-[17px] font-bold text-foreground'>
                            {staff.name}
                        </p>

                        <StaffStatusBadge status={status} />
                    </div>

                    <p className='mt-0.5 truncate text-[13px] text-muted-foreground'>
                        {[
                            staff.phone ? formatPhone(staff.phone) : null,
                            `${entry.completedToday} ${entry.completedToday === 1 ? 'car' : 'cars'} today`
                        ].filter(Boolean).join(' · ')}
                    </p>

                    {entry.breakPending && (
                        <p className='mt-0.5 text-xs font-semibold text-amber-800'>
                            Break starts after this vehicle
                        </p>
                    )}
                </div>

                <ChevronRight className='size-5 shrink-0 text-slate-300' aria-hidden='true' />
            </Link>

            <div className='mx-4 rounded-xl bg-surface px-3 py-2.5'>
                {currentJob
                    ? (
                        <Link href={`/vehicles/${currentJob.id}`} className='flex items-center justify-between gap-3 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'>
                            <span className='min-w-0'>
                                <Plate value={currentJob.plate} size='sm' />

                                <span className='mt-1 block truncate text-xs text-muted-foreground'>
                                    {`${currentJob.serviceName}${currentJob.startedAt ? `, started ${formatTime(currentJob.startedAt)}` : ', not started'}`}
                                    {jobs.length > 1 && `, +${jobs.length - 1} more`}
                                </span>
                            </span>

                            <JobClock job={currentJob} className='shrink-0 font-mono text-[15px] font-bold text-foreground' />
                        </Link>
                    )
                    : (
                        <p className='flex items-center gap-2 py-1 text-[13px] text-muted-foreground'>
                            <CarFront className='size-4' aria-hidden='true' />
                            {status === 'available' ? 'Free for the next vehicle' : status === 'break' ? 'On a break' : 'Not working today'}
                        </p>
                    )}
            </div>

            <div className='mt-auto grid grid-cols-3 gap-2 p-4 pt-3'>
                <Button
                    size='md'
                    variant={status === 'available' ? 'primary' : 'secondary'}
                    onClick={onAssign}
                    disabled={inactive}
                    aria-label={`Assign a vehicle to ${staff.name}`}
                    className='px-2'
                >
                    <CarFront aria-hidden='true' />
                    Assign
                </Button>

                <Button size='md' variant='secondary' onClick={onAvailability} aria-label={`Status of ${staff.name}`} className='px-2'>
                    <ToggleRight aria-hidden='true' />
                    Status
                </Button>

                <Button size='md' variant='secondary' onClick={onEdit} aria-label={`Edit ${staff.name}`} className='px-2'>
                    <PencilLine aria-hidden='true' />
                    Edit
                </Button>
            </div>
        </article>
    )
}
